// src/components/SendTracker.jsx
// 3.70.0 (owner, Oct 30): after Send, Compose shows each fax going out,
// step by step: (waiting for the line) → preparing the document → calling
// the receiver → sending the pages → delivered / not delivered. On for
// everyone; Skip goes to the Sent panel (its Watch it button comes back
// here) and the fax carries on in the background (Outbox shows it too).
// The steps are Telnyx's own (GET /portal/send-progress): queued →
// processed → sending → the result. Telnyx reports no page-by-page
// progress, so "Sending pages" shows the page count and a bar on the usual
// time; the result says how many pages went.
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from './Notifications';
import { getSendProgress, sendWaitingNow } from '../utils/api';
import { reasonText } from '../utils/faxReasons';
import { timeZone, tzLabel } from '../utils/timeZone';
import { useSendTimings } from '../hooks/useSendTimings';

const FINAL = ['delivered', 'failed'];
const POLL_MS = 3000;          // while a call is going out
const POLL_WAITING_MS = 10000; // while every fax waits for the line
const STOP_AFTER_MS = 2 * 3600 * 1000;

// Usual time on the line (from real sends: 6 pages ≈ 1¾ min, 27 ≈ 7½)
const usualMs = pages => Math.max(45, 20 + (Number(pages) || 1) * 20) * 1000;
const clock = ms => { const s = Math.max(0, Math.floor(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };
const at = iso => (iso ? `${new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: timeZone() })} ${tzLabel(new Date(iso))}` : '');
const pagesWord = n => `${n} page${n === 1 ? '' : 's'}`;

// items: [{ ref, faxId?, waiting?, label, fileName, part?, pages? }]
// progress: { [ref]: server row } — kept by Compose, so Skip / Watch it
// never loses what was seen
export default function SendTracker({ items, progress, setProgress, onSkip, onAnother, onOutbox, onOpenFax, onClose, inline }) {
  const { getToken } = useAuth();
  const { notify, confirm } = useNotify();
  const tm = useSendTimings();   // 3.75.0
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState('');
  const started = useRef(Date.now());
  const headRef = useRef(null);
  useEffect(() => { headRef.current?.focus(); }, []);

  const rowOf = it => progress[it.ref] || (it.waiting ? { state: 'waiting' } : { state: 'calling', faxId: it.faxId, sentAt: null });
  const rows = items.map(it => ({ it, p: rowOf(it) }));
  const done = rows.every(r => FINAL.includes(r.p.state));
  const allWaiting = rows.every(r => r.p.state === 'waiting');

  // The clock for elapsed times and the bar
  useEffect(() => {
    if (done) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [done]);

  // Asks the server while anything is still on its way (slower while every
  // fax waits for the line); stops at the results, on Skip, or after 2 hours
  const refs = items.map(i => i.ref).join(',');
  const waitingRef = useRef(allWaiting);
  waitingRef.current = allWaiting;
  useEffect(() => {
    if (done) return undefined;
    let live = true, t = null;
    const tick = async () => {
      try {
        const list = await getSendProgress(refs.split(','), getToken);
        if (live && list.length) setProgress(old => ({ ...old, ...Object.fromEntries(list.map(x => [x.ref, { ...old[x.ref], ...x }])) }));
      } catch { /* the next try */ }
      if (live && Date.now() - started.current < STOP_AFTER_MS) t = setTimeout(tick, waitingRef.current ? POLL_WAITING_MS : POLL_MS);
    };
    t = setTimeout(tick, 1200);
    return () => { live = false; clearTimeout(t); };
  }, [refs, done, getToken, setProgress]);

  async function sendNow(it, p) {
    const ok = await confirm({
      title: 'Send this fax now?',
      message: `It goes to ${it.label} now instead of waiting in line${p.slow ? `. This number is hard to reach, so faxes to it are normally spaced ${tm.hardSpacing} apart` : ''}. If a fax to that number is on the line right now, try again in a few minutes.`,
      confirmLabel: 'Send now',
    });
    if (!ok) return;
    setBusy(it.ref);
    try {
      await sendWaitingNow(p.queueId, getToken);
      setProgress(old => ({ ...old, [it.ref]: { state: 'starting', wasWaiting: true } }));
    } catch (e) { notify({ tone: 'error', title: "Couldn't send it now", detail: e.message }); }
    setBusy('');
  }

  const failed = rows.filter(r => r.p.state === 'failed').length;
  const many = rows.length > 1;
  const title = !done ? (allWaiting ? (many ? 'Your faxes are waiting for the line' : 'Your fax is waiting for the line') : (many ? 'Sending your faxes' : 'Sending your fax'))
    : failed === rows.length ? (many ? 'Not delivered' : 'Not delivered')
    : failed ? 'Some faxes weren’t delivered'
    : many ? 'All delivered' : 'Delivered';

  return (
    <div style={S.wrap} role="status" aria-live="polite">
      <div style={S.head}>
        <span style={{ ...S.headIcon, ...(done ? (failed ? S.headIconBad : S.headIconOk) : {}) }} aria-hidden="true" className={done ? 'trk-pop' : 'trk-float'}>
          {done ? (failed ? '!' : '✓') : <FaxGlyph />}
        </span>
        <h2 ref={headRef} tabIndex={-1} style={S.title}>{title}</h2>
      </div>
      <div style={S.cards}>
        {rows.map(({ it, p }) => (
          <Card key={it.ref} it={it} p={p} now={now} compact={many} busy={busy === it.ref}
            onSendNow={() => sendNow(it, p)} onOpen={p.faxId ? () => onOpenFax(p.faxId) : null} />
        ))}
      </div>
      {!done && <div style={S.note}>You can skip this: {many ? 'your faxes keep' : 'your fax keeps'} going, and you'll see {many ? 'them' : 'it'} in Outbox. You'll get an email when {many ? 'each is' : 'it’s'} delivered or if it fails.</div>}
      <div style={S.btns}>
        {done
          ? <button onClick={onAnother} style={S.main}>Send another fax</button>
          : <button onClick={onSkip} style={S.main}>Skip</button>}
        <button onClick={onOutbox} style={S.ghost}>Open Outbox</button>
        {!inline && <button onClick={onClose} style={S.ghost}>Close</button>}
      </div>
    </div>
  );
}

// One fax: its steps
function Card({ it, p, now, compact, busy, onSendNow, onOpen }) {
  const tm = useSendTimings();   // 3.75.0
  const st = p.state;
  const stage = p.stage || null;
  const final = FINAL.includes(st);
  const wasWaiting = it.waiting || p.wasWaiting;
  const pages = p.pages || it.pages || null;
  // Which step is on: 0 line, 1 preparing, 2 calling, 3 sending, 4 result
  const on = st === 'waiting' ? 0
    : final ? 4
    : stage === 'sending' ? 3
    : stage === 'processed' ? 2
    : 1;
  const since = Date.parse(p.stageAt || p.sentAt || '') || null;
  const steps = [];
  if (wasWaiting || st === 'waiting') steps.push({ k: 0, short: 'Line', label: 'Waiting for the line',
    detail: st === 'waiting' ? waitText(p, tm) : null });
  steps.push({ k: 1, short: 'Prepare', label: 'Preparing the document' });
  steps.push({ k: 2, short: 'Call', label: `Calling ${it.label}` });
  steps.push({ k: 3, short: 'Pages', label: pages ? `Sending ${pagesWord(pages)}` : 'Sending the pages',
    detail: on === 3 && since ? <SendBar since={since} pages={pages} now={now} /> : null });
  steps.push({ k: 4, short: st === 'failed' ? 'Not delivered' : 'Delivered', label: st === 'delivered' ? `Delivered${p.pages ? ` · ${pagesWord(p.pages)} went through` : ''}`
    : st === 'failed' ? 'Not delivered' : 'Delivered',
    detail: st === 'delivered' ? `At ${at(p.doneAt)}. It's in Outbox.`
      : st === 'failed' ? `${p.reason ? reasonText(p.reason) : (p.reasonText || 'Delivery failed')}. Open the fax to resend it.` : null,
    bad: st === 'failed' });

  const stateOf = s => (s.k < on ? 'done' : s.k === on ? (s.k === 4 ? (s.bad ? 'bad' : 'done') : 'on') : 'todo');
  const dot = (s, state) => (
    <span style={{ ...S.dot, ...S[`dot_${state}`] }} className={state === 'on' ? 'trk-ring' : state === 'done' || state === 'bad' ? 'trk-pop' : ''} aria-hidden="true">
      {state === 'done' ? '✓' : state === 'bad' ? '!' : ''}
    </span>
  );
  const extras = s => (
    <>
      {s.detail && <div style={S.detail}>{s.detail}</div>}
      {s.k === 0 && st === 'waiting' && p.canSendNow && (
        <button onClick={onSendNow} disabled={busy} style={S.smallBtn}>{busy ? 'Sending…' : 'Send now'}</button>
      )}
      {s.k === 4 && st === 'failed' && onOpen && <button onClick={onOpen} style={S.smallBtn}>Open this fax</button>}
    </>
  );

  // Several faxes: one row of steps each, and the step that's on spelled out
  if (compact) {
    const cur = steps.find(s => s.k === on) || steps[steps.length - 1];
    return (
      <div style={{ ...S.card, ...S.cardCompact }} className="trk-in">
        <div style={S.cardHead}>
          <span style={S.file}>{it.fileName}{it.part ? ` (part ${it.part.i} of ${it.part.n})` : ''}</span>
          {pages ? <span style={S.small}> · {pagesWord(pages)}</span> : null}
          <span style={S.small}> → {it.label}</span>
        </div>
        <ol style={S.row} aria-label={`Steps: now ${cur.label}`}>
          {steps.map((s, i) => {
            const state = stateOf(s);
            return (
              <li key={s.k} style={S.rowStep} aria-current={state === 'on' ? 'step' : undefined}>
                {i > 0 && <span style={{ ...S.link, ...(state === 'todo' ? {} : S.linkOn) }} aria-hidden="true" />}
                {dot(s, state)}
                <span style={{ ...S.rowLabel, ...(state === 'todo' ? S.todo : {}), ...(state === 'bad' ? S.badText : {}) }}>{s.short}</span>
              </li>
            );
          })}
        </ol>
        <div style={{ ...S.stepBody, padding: '6px 0 8px' }}>
          <div style={{ ...S.stepLabel, ...(cur.bad ? S.badText : {}) }}>
            {cur.label}
            {cur.k === on && on > 0 && on < 4 && since && <span style={S.elapsed}>{clock(now - since)}</span>}
          </div>
          {extras(cur)}
        </div>
      </div>
    );
  }

  return (
    <div style={S.card} className="trk-in">
      <div style={S.cardHead}>
        <span style={S.file}>{it.fileName}{it.part ? ` (part ${it.part.i} of ${it.part.n})` : ''}</span>
        {pages ? <span style={S.small}> · {pagesWord(pages)}</span> : null}
      </div>
      <ol style={S.steps}>
        {steps.map(s => {
          const state = stateOf(s);
          return (
            <li key={s.k} style={S.step} aria-current={state === 'on' ? 'step' : undefined}>
              {dot(s, state)}
              <div style={S.stepBody}>
                <div style={{ ...S.stepLabel, ...(state === 'todo' ? S.todo : {}), ...(state === 'bad' ? S.badText : {}) }}>
                  {s.label}
                  {state === 'on' && since && s.k > 0 && <span style={S.elapsed}>{clock(now - since)}</span>}
                </div>
                {state !== 'todo' && extras(s)}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

const waitText = (p, tm) => {
  if (!p.position && p.etaMin == null) return 'Another fax to this number goes first.';
  return `${p.position ? `${p.position} of ${p.of} in line · ` : ''}goes out in ${Math.max(1, Math.ceil(p.etaMin || 0))} min or less`
    + (p.hours ? `, once office hours start (${p.hours})` : '')
    + (p.slow ? `. This number is hard to reach, so faxes to it go out ${tm.hardSpacing} apart.` : '');
};

// The pages bar: moves on the usual time for this many pages and slows
// near the end; only the real result finishes it
function SendBar({ since, pages, now }) {
  const pct = Math.round(96 * (1 - Math.exp(-Math.max(0, now - since) / usualMs(pages))));
  const mins = Math.max(1, Math.round(usualMs(pages) / 60000));
  return (
    <div>
      <div style={S.track} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label="Sending the pages">
        <div style={{ ...S.fill, width: `${pct}%` }}><div style={S.shine} /></div>
      </div>
      <div style={S.small}>{pages ? `${pagesWord(pages)} usually take${pages === 1 ? 's' : ''} about ${mins} min` : 'Usually a few minutes'}</div>
    </div>
  );
}

function FaxGlyph() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 9V3h8l2 2v4" /><rect x="3" y="9" width="18" height="9" rx="2" /><path d="M7 18v3h10v-3" /><path d="M7 13h4" />
      <path className="trk-sheet" d="M9 15.5h6" />
    </svg>
  );
}

const S = {
  wrap: { padding: '28px 22px 26px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 },
  head: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center' },
  headIcon: { width: 56, height: 56, borderRadius: '50%', background: 'var(--c-e0f2f1)', color: 'var(--t-00897b)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, fontWeight: 800 },
  headIconOk: { background: 'var(--c-e8f5e9)', color: 'var(--t-2e7d32)' },
  headIconBad: { background: 'var(--c-fff3e0)', color: 'var(--t-e65100)' },
  title: { margin: 0, fontSize: 21, color: 'var(--t-263238)', outline: 'none' },
  cards: { display: 'flex', flexDirection: 'column', gap: 12, width: '100%', maxWidth: 560 },
  card: { border: '1px solid var(--c-e0f2f1)', borderRadius: 12, padding: '14px 16px 6px', background: 'var(--c-f9fafb)', textAlign: 'left', minWidth: 0 },
  cardCompact: { padding: '10px 14px 4px' },
  row: { listStyle: 'none', margin: '2px 0 0', padding: 0, display: 'flex', alignItems: 'flex-start' },
  rowStep: { flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 0 },
  rowLabel: { fontSize: 11.5, fontWeight: 600, color: 'var(--t-37474f)', textAlign: 'center', overflowWrap: 'anywhere' },
  link: { position: 'absolute', top: 11, right: '50%', width: '100%', height: 2, background: 'var(--c-cfd8dc)', zIndex: 0 },
  linkOn: { background: 'var(--c-00897b)' },
  cardHead: { fontSize: 13, color: 'var(--t-37474f)', marginBottom: 8, overflowWrap: 'anywhere' },
  file: { fontWeight: 700 },
  small: { fontSize: 12, color: 'var(--t-78909c)' },
  steps: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column' },
  step: { display: 'flex', gap: 12, alignItems: 'flex-start', minHeight: 34, paddingBottom: 8 },
  dot: { position: 'relative', zIndex: 1, flex: '0 0 auto', width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, marginTop: 1, boxSizing: 'border-box' },
  dot_todo: { border: '2px solid var(--c-cfd8dc)', background: 'var(--c-ffffff)' },
  dot_on: { border: '2px solid var(--c-00897b)', background: 'var(--c-ffffff)', boxShadow: 'inset 0 0 0 4px var(--c-ffffff), inset 0 0 0 11px var(--c-00897b)' },
  dot_done: { background: 'var(--c-00897b)', color: 'var(--t-ffffff)' },
  dot_bad: { background: 'var(--c-e65100)', color: 'var(--t-ffffff)' },
  stepBody: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 },
  stepLabel: { fontSize: 14, color: 'var(--t-263238)', fontWeight: 600, display: 'flex', justifyContent: 'space-between', gap: 10, overflowWrap: 'anywhere' },
  todo: { color: 'var(--t-90a4ae)', fontWeight: 500 },
  badText: { color: 'var(--t-c62828)' },
  elapsed: { fontSize: 12, fontWeight: 600, color: 'var(--t-546e7a)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  detail: { fontSize: 12.5, color: 'var(--t-546e7a)', lineHeight: 1.5 },
  track: { height: 7, borderRadius: 7, background: 'var(--c-e0f2f1)', overflow: 'hidden', margin: '2px 0 4px' },
  fill: { position: 'relative', height: '100%', borderRadius: 7, overflow: 'hidden', background: 'linear-gradient(90deg, var(--c-4db6ac), var(--c-00897b))', transition: 'width 1s linear' },
  shine: { position: 'absolute', inset: 0, background: 'linear-gradient(90deg, transparent, rgb(var(--c-ffffff-rgb) / 0.55), transparent)', animation: 'faxShine 1.4s ease-in-out infinite' },
  smallBtn: { alignSelf: 'flex-start', padding: '5px 12px', fontSize: 12.5, fontWeight: 700, borderRadius: 7, border: '1px solid var(--c-00897b)', background: 'var(--c-ffffff)', color: 'var(--t-00897b)', cursor: 'pointer', fontFamily: 'inherit' },
  note: { fontSize: 13, color: 'var(--t-607d8b)', maxWidth: 480, textAlign: 'center' },
  btns: { display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', marginTop: 4 },
  main: { padding: '12px 22px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 15, fontWeight: 700, fontFamily: 'inherit' },
  ghost: { padding: '12px 18px', background: 'var(--c-ffffff)', color: 'var(--t-546e7a)', border: '1px solid var(--c-cfd8dc)', borderRadius: 8, cursor: 'pointer', fontSize: 15, fontWeight: 600, fontFamily: 'inherit' },
};
